import {
	MouseEvent,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	ensureNotNil,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { SideNavigation } from "./SideNavigation";
import { GenericNavigationPanelProps, SubNavigation } from "./SubNavigation";
import {
	MainMenuStoreData,
	useMainMenuStore,
	WorkspaceHandler
} from "client/Features/Layout/mainMenuStore";
import {
	DragOverEventArgs,
	DragOverEventResult,
	MediaQueryKind,
	useDragAndDropEvents,
	useEffectSkipFirstRender,
	useIsTouchDevice,
	usePointerEventOutside,
	useWorkspaceMediaQuery
} from "client/Hooks";
import classNames from "classnames";
import {
	useGlobalNavigationSidebarPinnedState,
	useHoveredItemState
} from "./useGlobalNavigationVisibilityState";
import { AppType, DestinationWindow } from "client/Api/Apps/common";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import {
	GlobalNavigationCriteria,
	useGlobalNavigationStore
} from "client/Features/Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import {
	ContainsDropOversWrapper,
	FontIconName,
	Portal
} from "client/Components";
import { SpacePanel } from "./SubNavigationContent/SpacePanel";
import { DocumentPanel } from "./SubNavigationContent/DocumentPanel";
import { EmailPanel } from "./SubNavigationContent/EmailPanel";
import { HubPanel } from "./SubNavigationContent/HubPanel";
import { AddressBookPanel } from "./SubNavigationContent/AddressBookPanel";
import { createRoot } from "react-dom/client";
import { SettingsPanel } from "./SubNavigationContent/SettingsPanel";
import { ActivePanelOverride } from "./ActivePanelOverride";
import styles from "./GlobalNavigationSidebar.module.less";
import subNavigationStyles from "./SubNavigation.module.less";
import { useActivePanelOverrideStore } from "./useActivePanelOverrideStore";
import { EMAIL_DRAGGABLE_DATA_IDENTIFIER } from "client/Features/Email/dragAndDrop";
import { navigateToAddressBookApp, navigateToHubApp } from "client_legacy/Apps/appNavigation";
import { safeSessionStorage } from "client_legacy/ndaw/safeStorage";
import { localize } from "client_legacy/ndaw/culture";
import { CalendarPanel } from "./SubNavigationContent/CalendarPanel/CalendarPanel";
import { CalendarClient, getEffectiveCalendarClient } from "client/Features/Calendar/calendarClient";
import { useSubNavigationWidthState } from "./useSubNavigationWidthState";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { AppStoreDialog } from "client/Features/Apps/Store/AppStoreDialog";

const CROSS_PAGE_VISIBLE_ITEM_KEY = "global-navigation-sidebar:cross-page-visible-item";

let isAlreadyMounted: boolean = false;

const host: HTMLElement | null = document.getElementById("page-collapsible-sidebar-host");

export type MenuItemKind = NavigationItemType | "app-store";

export const GlobalNavigationSidebar = (): ReactElement => {
	const [didPinnedStateChangeManually, setDidPinnedStateChangeManually] = useState<boolean>(false);
	const { hoveredItem, setHoveredItem } = useHoveredItemState();
	const { isPinned, setIsPinned } = useGlobalNavigationSidebarPinnedState();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isTouch: boolean = useIsTouchDevice();
	const { activeNavigationItem, changeActiveMenuItem } = useSideNavigationMenu();
	const availableMenuItems: readonly AvailableMenuItem[] = useAvailableMenuItems();
	const [doesContainDropOvers, setDoesContainDropOvers] = useState<boolean>(false);
	const [hasDragOver, setHasDragOver] = useState<boolean>(false);

	const { isAppStoreOpen, setIsAppStoreOpen } = useGlobalNavigationStore(
		(x) => ({
			isAppStoreOpen: x.isAppStoreOpen,
			setIsAppStoreOpen: x.setIsAppStoreOpen
		}),
		shallowComparison
	);

	const {
		hasOverride,
		doesOverrideContainDropOvers
	} = useActivePanelOverrideStore(
		x => ({
			doesOverrideContainDropOvers: x.doesOverrideContainDropOvers,
			hasOverride: x.hasOverride
		}),
		shallowComparison
	);

	const [activeItemWidth, resizeActiveItemHandler] = useSubNavigationWidthState(activeNavigationItem);

	const shouldReserveSpaceForPinnedPanel = useMemo(
		(): boolean => {
			return isNotNil(
				availableMenuItems.find(x => x.kind === activeNavigationItem)
					?.panel
			);
		},
		[activeNavigationItem, availableMenuItems]
	);

	const isDesktopTouchDevice = !isResponsive && isTouch;

	const handleSideNavMenuItemMouseEnter = useCallback(
		(item: MenuItemKind): void => {
			if (isDesktopTouchDevice) {
				return;
			}

			setHoveredItem(
				activeNavigationItem === item && isPinned
					? undefined
					: item
			);
		},
		[
			activeNavigationItem,
			isDesktopTouchDevice,
			isPinned,
			setHoveredItem
		]
	);

	const unhoverItemHandler = useCallback(
		(): void => {
			setHoveredItem(undefined);
		},
		[setHoveredItem]
	);

	const mobileBackdropClickHandler = useCallback(
		(e: MouseEvent): void => {
			e.stopPropagation();
			setIsPinned(false);
		},
		[setIsPinned]
	);

	const sideNavClickHandler = useCallback(
		(): void => {
			if (isResponsive) {
				setIsPinned(true);
			}
		},
		[
			isResponsive,
			setIsPinned
		]
	);

	const pinnedChangeHandler = useCallback(
		(isPinned: boolean): void => {
			setDidPinnedStateChangeManually(true);
			setIsPinned(isPinned);
		},
		[setIsPinned]
	);

	const activateItemHandler = useCallback(
		(item: MenuItemKind): void => {
			if (isTouch && !isPinned) {
				if (activeNavigationItem === item) {
					setHoveredItem(isNil(hoveredItem) ? item : undefined);
					return;
				}

				safeSessionStorage.setItem(
					CROSS_PAGE_VISIBLE_ITEM_KEY,
					item
				);
			}

			changeActiveMenuItem(item);
		},
		[
			isTouch,
			isPinned,
			changeActiveMenuItem,
			activeNavigationItem,
			setHoveredItem,
			hoveredItem
		]
	);

	useEffect(
		(): (() => void) => {
			if (isAlreadyMounted) {
				throw new Error("Multiple instances of GlobalNavigationSidebar have been rendered.");
			}
			isAlreadyMounted = true;

			return (): void => {
				isAlreadyMounted = false;
			};
		},
		[]
	);

	useEffectSkipFirstRender(
		(): void => {
			if (!isTouch || isPinned) {
				safeSessionStorage.removeItem(CROSS_PAGE_VISIBLE_ITEM_KEY);
				return;
			}

			const value: MenuItemKind | nil = safeSessionStorage
				.getItem<MenuItemKind>(CROSS_PAGE_VISIBLE_ITEM_KEY);

			safeSessionStorage.removeItem(CROSS_PAGE_VISIBLE_ITEM_KEY);

			if (isNotNil(value) && value === activeNavigationItem) {
				setHoveredItem(value);
			}
		},
		[
			activeNavigationItem,
			isPinned,
			isTouch,
			setHoveredItem
		]
	);

	usePointerEventOutside(
		unhoverItemHandler,
		ensureNotNil(host)
	);

	const doesSomeItemAcceptDraggableDataTypes = useCallback(
		(types: readonly string[]): boolean => {
			return availableMenuItems.some(
				menuItem => canHandleDraggableTypes(
					menuItem.allowedDraggableDataType,
					menuItem.kind === activeNavigationItem,
					types
				)
			);
		},
		[activeNavigationItem, availableMenuItems]
	);

	const dragEnterHandler = useCallback(
		(args: DragOverEventArgs): void => {
			if (doesSomeItemAcceptDraggableDataTypes(args.types)) {
				setHasDragOver(true);
			}
		},
		[doesSomeItemAcceptDraggableDataTypes]
	);

	const dragLeaveHandler = useCallback(
		(): void => {
			setHasDragOver(false);
		},
		[]
	);

	const dragOverHandler = useCallback(
		(args: DragOverEventArgs): DragOverEventResult => {
			const isAcceptedBySomeItem: boolean = doesSomeItemAcceptDraggableDataTypes(args.types);

			if (!isAcceptedBySomeItem) {
				return { isAccepted: false };
			}

			return {
				isAccepted: true,
				dropEffect: "move"
			};
		},
		[doesSomeItemAcceptDraggableDataTypes]
	);

	const {
		isDropTarget: _,
		...dragAndDropEventHandlers
	} = useDragAndDropEvents({
		onDragOver: dragOverHandler,
		onDragEnter: dragEnterHandler,
		onDragLeave: dragLeaveHandler
	});

	const isPanelVisible: boolean = doesContainDropOvers
		|| (
			hasOverride
			&& doesOverrideContainDropOvers
		)
		|| hasDragOver
		|| (
			isNotNil(hoveredItem)
			&& isDesktopTouchDevice
		);

	return (
		<>
			{(!isResponsive || !isNil(activeNavigationItem)) && (
				<Portal container={ensureNotNil(host)}>
					<div
						{...setTrackingId("global-navigation-sidebar-backdrop")}
						className={
							classNames(
								styles.responsiveBackdrop,
								{
									[styles.responsiveBackdrop_isVisible]: isPinned
								}
							)
						}
						onClick={mobileBackdropClickHandler}
					/>
					<aside
						{...dragAndDropEventHandlers}
						{...setTrackingId("global-navigation-sidebar")}
						className={classNames(
							styles.root,
							subNavigationStyles.hoverRoot,
							{ [subNavigationStyles.hoverRoot_hover]: isPanelVisible }
						)}
					>
						<ContainsDropOversWrapper onDoesContainDropOversChange={setDoesContainDropOvers}>
							<div
								className={styles.sideNavWrapper}
								onClick={sideNavClickHandler}
							>
								{!isResponsive && (
									<SideNavigation
										activeMenuItem={activeNavigationItem}
										onActivateItem={activateItemHandler}
										availableMenuItems={availableMenuItems}
										onItemMouseEnter={handleSideNavMenuItemMouseEnter}
									/>
								)}
							</div>
							<div
								style={isNotNil(activeItemWidth)
									? {
										"--global-navigation-sidebar-sub-navigation-width": `${activeItemWidth}px`
									}
									: undefined
								}
								className={
									classNames(
										styles.subNavigationWrapper,
										{
											[styles.subNavigationWrapper_isPinned]: isNotNil(activeNavigationItem) && isPinned,
											[styles.subNavigationWrapper_hasPanel]: shouldReserveSpaceForPinnedPanel,
											[styles.subNavigationWrapper_allowWidthAnimation]: didPinnedStateChangeManually
										}
									)
								}
							>
								<SubNavigation
									activePanel={activeNavigationItem}
									isPinned={isNotNil(activeNavigationItem) && isPinned}
									onPinnedChange={pinnedChangeHandler}
									hoveredItem={hoveredItem}
									availableMenuItems={availableMenuItems}
									onUnhover={unhoverItemHandler}
									onResizeActivePanel={resizeActiveItemHandler}
									isResponsive={isResponsive}
								/>
							</div>
						</ContainsDropOversWrapper>
					</aside>
				</Portal>
			)}

			{isAppStoreOpen && (
				<AppStoreDialog
					onClose={() => setIsAppStoreOpen(false)}
				/>
			)}
		</>
	);
};

GlobalNavigationSidebar.displayName="GlobalNavigationSidebar";
GlobalNavigationSidebar.ActivePanelOverride = ActivePanelOverride;

export type AvailableMenuItemDraggableDataType =
	| string
	| {
		kind: "active-only";
		type: string;
	}
	| {
		kind: "inactive-only";
		type: string;
	};

export type AvailableMenuItem = {
	kind: MenuItemKind;
	icon: FontIconName;
	name: string;
	panel: React.FC<GenericNavigationPanelProps> | undefined;
	allowedDraggableDataType?: AvailableMenuItemDraggableDataType;
	position: "main" | "footer";
	trackingId: string;
};

function useAvailableMenuItems(): readonly AvailableMenuItem[] {
	const {
		isAddressBookAvailable,
		isHubAppAvailable,
		isDocumentsAppAvailable
	} = useMainMenuStore(
		(x: MainMenuStoreData) => ({
			isAddressBookAvailable: x.isAddressBookAvailable,
			isHubAppAvailable: x.isHubAppAvailable,
			isDocumentsAppAvailable: x.isDocumentsAppAvailable
		}),
		shallowComparison
	);

	return useMemo(
		(): AvailableMenuItem[] => {
			const availableMenuItems: AvailableMenuItem[] = [
				{
					kind: "space",
					icon: "rocket",
					panel: SpacePanel,
					position: "main",
					trackingId: "spaces",
					name: localize("_Spaces")
				}
			];

			if (isDocumentsAppAvailable) {
				availableMenuItems.push({
					kind: AppType.Documents,
					icon: "folder-open",
					panel: DocumentPanel,
					position: "main",
					trackingId: "documents",
					name: localize("_Documents")
				});
			}

			const effectiveEmailClient: EmailClient = getEffectiveEmailClient();

			if (effectiveEmailClient.supportsOverview) {
				availableMenuItems.push({
					kind: AppType.Email,
					icon: "envelope",
					panel: effectiveEmailClient.isExternal
						? undefined
						: EmailPanel,
					allowedDraggableDataType: {
						kind: "active-only",
						type: EMAIL_DRAGGABLE_DATA_IDENTIFIER
					},
					position: "main",
					trackingId: "email",
					name: localize("_Email")
				});
			}

			if (isHubAppAvailable) {
				availableMenuItems.push({
					kind: AppType.Hub,
					icon: "the-hub",
					panel: HubPanel,
					position: "main",
					trackingId: "hub",
					name: localize("_TheHub")
				});
			}

			if (isAddressBookAvailable) {
				availableMenuItems.push({
					kind: AppType.AddressBook,
					icon: "address-book",
					panel: AddressBookPanel,
					position: "main",
					trackingId: "address-book",
					name: localize("_AddressBook")
				});
			}

			const effectiveCalendarClient: CalendarClient = getEffectiveCalendarClient();
			if (effectiveCalendarClient.doesSupportOverview) {
				availableMenuItems.push({
					kind: AppType.Office365Calendar,
					icon: "calendar",
					panel: effectiveCalendarClient.isExternal
						? undefined
						: CalendarPanel,
					position: "main",
					trackingId: "calendar",
					name: localize("_Calendar")
				});
			}

			availableMenuItems.push({
				kind: "app-store",
				icon: "grid-2",
				panel: undefined,
				position: "main",
				trackingId: "app-store",
				name: localize("_AppStore")
			});

			availableMenuItems.push({
				kind: "settings",
				icon: "gear",
				panel: SettingsPanel,
				position: "footer",
				trackingId: "settings",
				name: localize("_Settings")
			});

			return availableMenuItems;
		},
		[
			isAddressBookAvailable,
			isDocumentsAppAvailable,
			isHubAppAvailable
		]
	);
}

export function canHandleDraggableTypes(
	allowedDraggableDataType: AvailableMenuItemDraggableDataType | undefined,
	isActive: boolean,
	types: readonly string[]
): boolean {
	if (!allowedDraggableDataType) {
		return false;
	}

	let allowedDraggableDataTypeValue: string | undefined;

	if (typeof allowedDraggableDataType === "object") {
		if (allowedDraggableDataType.kind === "active-only" && !isActive) {
			return false;
		}

		if (allowedDraggableDataType.kind === "inactive-only" && isActive) {
			return false;
		}

		allowedDraggableDataTypeValue = allowedDraggableDataType.type;
	} else {
		allowedDraggableDataTypeValue = allowedDraggableDataType;
	}

	return types.some(t => t === allowedDraggableDataTypeValue);
}

type SideNavigationMenuHandler = {
	activeNavigationItem: NavigationItemType | undefined;
	changeActiveMenuItem: (item: MenuItemKind) => void;
};

function useSideNavigationMenu(): SideNavigationMenuHandler {
	const { activeCriteria, setIsAppStoreOpen } = useGlobalNavigationStore(
		(x) => ({
			activeCriteria: x.activeCriteria,
			setIsAppStoreOpen: x.setIsAppStoreOpen
		}),
		shallowComparison
	);

	const workspaceHandler: WorkspaceHandler | undefined = useMainMenuStore(x => x.workspaceHandler);

	const activeNavigationItem = useMemo(
		(): NavigationItemType | undefined => {
			return getActiveNavigationItemType(activeCriteria);
		},
		[activeCriteria]
	);

	const activateItemHandler = useCallback(
		(item: MenuItemKind): void => {
			switch (item) {
				case "space":
					if (workspaceHandler) {
						workspaceHandler.openDefaultSpace();
						return;
					}

					navigateRelativeToEnvironment("/", false);
					break;
				case "settings":
					navigateRelativeToEnvironment("/UserProfile", false);
					break;
				case "app-store":
					setIsAppStoreOpen(true);
					break;
				case AppType.Documents:
					const { destinationWindow } = userConfiguration().document;

					navigateRelativeToEnvironment(
						"/Documents",
						destinationWindow === DestinationWindow.NewWindow
					);
					break;
				case AppType.AddressBook:
					navigateToAddressBookApp();
					break;
				case AppType.Hub:
					navigateToHubApp();
					break;
				case AppType.Email:
					const emailClient: EmailClient = getEffectiveEmailClient();
					if (!emailClient.supportsOverview) {
						throw new Error("Client doesn't support the overview");
					}

					emailClient.openOverview({});
					break;
				case AppType.Office365Calendar:
					const calendarClient: CalendarClient = getEffectiveCalendarClient();

					if (!calendarClient.doesSupportOverview) {
						throw new Error("Client doesn't support the overview");
					}

					calendarClient.openOverview({
						isInNewWindow: calendarClient.isExternal
							? undefined
							: false
					});
					break;
				default:
					throw new Error(`There is no click action for the item ${item}`);
			}
		},
		[setIsAppStoreOpen, workspaceHandler]
	);

	return {
		activeNavigationItem,
		changeActiveMenuItem: activateItemHandler
	};
}

type NavigationItemType = AppType | "space" | "settings";

function getActiveNavigationItemType(
	activeCriteria: GlobalNavigationCriteria | undefined
): NavigationItemType | undefined {
	if (isNil(activeCriteria)) {
		return undefined;
	}

	if (activeCriteria.type === "app") {
		return activeCriteria.appType;
	}

	if (activeCriteria.type === "settings") {
		return "settings";
	}

	if (activeCriteria.type === "space" || activeCriteria.type === "embedApp") {
		return "space";
	}

	return undefined;
}

export function init(): void {
	createRoot(
		ensureNotNil(host)
	)
		.render(
			<GlobalNavigationSidebar />
		);
}